import { Flex, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { defaults } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { ReactElement } from 'react';

import { OnboardingProvider } from '@inflearn/modals';
import { SettingLayout } from '../../../../FSD/shared/ui/layout/SettingLayout';
import { BasicSection } from '../../../../components/pages/my/settings/account/BasicSection';
import { CareerSection } from '../../../../components/pages/my/settings/account/CareerSection';
import { OnboardingModalReentrySection } from '../../../../components/pages/my/settings/account/OnboardingModalReentrySection';
import { ProfileSection } from '../../../../components/pages/my/settings/account/ProfileSection';
import { LeaveServiceSection } from '../../../../components/pages/my/settings/account/leave/LeaveServiceSection';
import useClientAccountCredentialsQuery from '../../../../queries/useClientAccountCredentialsQuery';
import { useUserOnboardingsAnswersQuery } from '../../../../queries/useUserOnboardingsAnswersQuery';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
  revalidate: 60,
});

export default function AccountPage() {
  const { data } = useClientAccountCredentialsQuery();
  const { data: savedAnswerData } = useUserOnboardingsAnswersQuery({});
  const { lang } = useLang();

  const accountInfo = defaults(data, {
    id: 0,
    email: '',
    phoneNumber: '',
    hasPassword: false,
  });

  return (
    <Flex direction="column" gap="md">
      <ProfileSection />
      <BasicSection />
      <Flex
        gap="md"
        css={{
          [desktopFirstMediaQuery.mobile]: {
            flexDirection: 'column',
          },
        }}
      >
        <Flex css={{ flex: 1, minWidth: 0 }}>
          <OnboardingProvider lang={lang}>
            <OnboardingModalReentrySection userInterests={savedAnswerData?.categories} />
          </OnboardingProvider>
        </Flex>
        <KoOnly>
          <Flex css={{ flex: 1, minWidth: 0 }}>
            <CareerSection />
          </Flex>
        </KoOnly>
      </Flex>
      <LeaveServiceSection accountInfo={accountInfo} />
    </Flex>
  );
}

function AccountPageLayout({ children }: { children: ReactElement }) {
  const { t } = useTranslation();
  return (
    <SettingLayout title={t('메타.계정정보.제목')} description={t('메타.계정정보.설명')}>
      {children}
    </SettingLayout>
  );
}

AccountPage.getLayout = (page: ReactElement) => <AccountPageLayout>{page}</AccountPageLayout>;
